import type { ReactNode, ElementType } from 'react';
import type { MotionProps } from 'framer-motion';
import type { BoxProps } from '@mui/material/Box';

import { motion as fm } from 'framer-motion';

import Box from '@mui/material/Box';

import { motion as motionTokens } from '../../theme/motion';

// ----------------------------------------------------------------------

const MotionBox = fm.create(Box);

// Built from named BoxProps + MotionProps (not `ComponentProps<typeof MotionBox>`,
// whose inferred type isn't portably nameable in the emitted .d.ts — TS2742).
// `component` is re-declared because the non-polymorphic BoxProps default drops
// Box's polymorphic `component` prop.
type MotionBoxProps = Omit<BoxProps, keyof MotionProps> & MotionProps & { component?: ElementType };

export type PageTransitionProps = Omit<MotionBoxProps, 'children'> & {
  /** Identity of the current view. Changing it swaps content in place. */
  transitionKey: string;
  children: ReactNode;
};

/**
 * Instant content swap with a quick fade-in for keyed views (settings tabs,
 * route content). Frequently-navigated UI must NOT make the user wait, so there
 * is deliberately **no** `mode="wait"` exit gap and **no** slide: changing the
 * key remounts the new view, which appears immediately and just fades in
 * (~120ms). The previous design (mode="wait" + y-slide, ~480ms round trip) read
 * as a fake loader — you waited on an empty panel before the new tab showed.
 * Opacity-only, so reduced-motion users get the same (gentle) fade.
 */
export function PageTransition({ transitionKey, children, ...rest }: PageTransitionProps) {
  return (
    <MotionBox
      key={transitionKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: motionTokens.durationMs.instant / 1000,
        ease: motionTokens.easeArray.out,
      }}
      {...rest}
    >
      {children}
    </MotionBox>
  );
}
